import firebase from "firebase/app";
import "firebase/auth";
import Cookies from "js-cookie";
import { Loaders } from "shared/types/loaders";
import { ReducerTypes } from "shared/types/reducerTypes";
import apiCalls from "./apiCalls";
import wrapGlobalLoader from "./wrapGlobalLoader";

const useLogin = ({ dispatch }: { dispatch: any }) => {
  const getUser = wrapGlobalLoader(
    async () => {
      const result = await apiCalls({
        method: "get",
        url: "/user",
      });

      if (result) {
        dispatch({
          type: ReducerTypes.getUser,
          payload: { value: result.data },
        });
      } else {
        dispatch({
          type: ReducerTypes.removeUser,
        });
      }
    },
    dispatch,
    Loaders.fetchingUser
  );

  const getUserSolutions = async (
    uid: string,
    filter: "inProgress" | "submitted" = "submitted"
  ) => {
    const res = await apiCalls({
      method: "get",
      url: `/solutions/user/${uid}?filter=${filter}`,
    });

    if (res) {
      dispatch({
        type: ReducerTypes.getUserSolutions,
        payload: { value: res.data },
      });
    }
  };

  const getUserBadges = async (uid: string) => {
    const res = await apiCalls({
      method: "get",
      url: `/user-badges/${uid}`,
    });

    if (res) {
      dispatch({
        type: ReducerTypes.getUserBadges,
        payload: { value: res.data },
      });
    }
  };

  const updateUser = async (user: any) => {
    const res = await apiCalls(
      {
        method: "post",
        url: "/user",
        data: { ...user },
      },
      dispatch
    );

    if (res) {
      await getUser();
    }
  };

  const loginWithGithub = wrapGlobalLoader(
    async () => {
      try {
        const provider = new firebase.auth.GithubAuthProvider();

        await firebase
          .auth()
          .setPersistence(firebase.auth.Auth.Persistence.LOCAL);

        const user = await firebase.auth().signInWithPopup(provider);
        const token = await firebase.auth().currentUser?.getIdToken();

        const now: number = new Date().getTime();

        Cookies.remove("userToken");
        Cookies.remove("csrf");
        Cookies.remove("session");

        Cookies.set("csrf", now.toString(), { expires: 365 });

        const res = await apiCalls(
          {
            method: "post",
            data: {
              idToken: token,
              csrfToken: Cookies.get("csrf"),
            },
            url: `/sessionLogin`,
          },
          dispatch
        );

        if (res) {
          Cookies.set("session", res.data.token, { expires: 365 });
        }

        if (token) {
          Cookies.set("userToken", token, { expires: 365 });

          const {
            name,
            login,
            picture,
            avatar_url,
            location,
            bio,
            blog,
            html_url,
            email,
            twitter_username,
          } = user?.additionalUserInfo?.profile as any;

          const { username }: any = user?.additionalUserInfo;

          await updateUser({
            name: name ? name : login,
            picture: avatar_url ? avatar_url : picture,
            location,
            bio,
            blog,
            githubUrl: html_url,
            email,
            twitter_username,
            username,
          });
        }
      } catch (error) {
        alert("Something went wrong, please try again");
      }
    },
    dispatch,
    Loaders.fetchingUser
  );

  const signOut = wrapGlobalLoader(
    async () => {
      try {
        await firebase.auth().signOut();

        Cookies.remove("userToken");
        Cookies.remove("csrf");
        Cookies.remove("session");

        await getUser();

        dispatch({
          type: ReducerTypes.resetUser,
        });
      } catch (error) {
        alert("Something went wrong");
      }
    },
    dispatch,
    Loaders.fetchingUser
  );

  return {
    signOut,
    loginWithGithub,
    getUser,
    updateUser,
    getUserSolutions,
    getUserBadges,
  };
};

export default useLogin;
